import { Injectable } from '@angular/core';
import { EMPTY, Observable, of } from 'rxjs';
import { Order } from '../../order/models/order';
import { Router } from '@angular/router';
import { PaymentProvider } from '../models/payment-provider';
import { PaymentService } from './payment.service';
import { GiftCard } from '../../giftcard/models/giftcard';
import { LoggerService } from '../../../services/logger/logger.service';
import { OrderStateService } from '../../order/services/order-state.service';
import { DiscountStateService } from './discount-state.service';
import { PaymentGiftStateService } from './payment-gift-state.service';
import { PrepaidPaymentInfo } from '../models/prepaid-payment-info';
import { faCcPaypal, IconDefinition } from '@fortawesome/free-brands-svg-icons';
import { faCreditCard, faMoneyBillAlt, IconDefinition as IconDefinitionFas, faIdCard } from '@fortawesome/free-solid-svg-icons';
import { PartnerCodeStateService } from '../../partner-code/services/partner-code-state.service';
import { ShopSettingsService } from '../../general/services/shop-settings.service';
import { map, switchMap, switchMapTo } from 'rxjs/operators';
import { OrderTypeEnum } from '../../order/models/order-type.enum';
import { OrderLine } from '../../order/models/order-line';
import { GiftCardStepService } from '../../giftcard/services/giftcard-step.service';
import { PartnerStateService } from '../../partner/services/partner-state.service';


@Injectable()
export class PaymentStateService {
  private _selectedProvider: PaymentProvider;
  private _paymentFormHtml = '';
  private _isCashPayment: boolean;
  partnerCodeCardNumber = '';
  private _paymentEnabled = true;
  private extraPaymentLine: OrderLine;

  accountPaymentProviderName = 'Account';
  accountPaymentProvider = new PaymentProvider(100, this.accountPaymentProviderName, 'ACCOUNT_NAME', true);

  constructor(
    private paymentService: PaymentService,
    private logger: LoggerService,
    private orderStateService: OrderStateService,
    private discountStateService: DiscountStateService,
    private paymentGiftStateService: PaymentGiftStateService,
    private partnerCodeStateService: PartnerCodeStateService,
    private shopSettingsService: ShopSettingsService,
    private giftCardStepService: GiftCardStepService,
    private partnerStateService: PartnerStateService,
  ) {
  }

  get paymentProviders(): Observable<PaymentProvider[]> {
    return this.paymentService.getPaymentProviders().pipe(
      map(paymentProviders => {
        if (this.isPartnerCodePaymentAllowed) {
          paymentProviders.push(this.accountPaymentProvider);
        }
        return paymentProviders;
      })
    );
  }

  get paymentFormHtml(): string {
    return this._paymentFormHtml;
  }

  private get _order(): Order {
    return this.orderStateService.order;
  }

  startPayment(): Observable<boolean> {
    return of(false).pipe(
      switchMap(_ => {
        if (!this._selectedProvider) {
          return EMPTY;
        }

        if (this._selectedProvider.name === this.accountPaymentProviderName) {
          return this._startAccountPayment();
        } else {
          return this._startPayment();
        }
      }),
    );
  }

  private _startPayment(): Observable<boolean> {
    return of(false).pipe(
      switchMap(_ => {
        const redeemedGiftCards = this.paymentGiftStateService.redeemedGiftCards;
        const orderId = this.orderStateService.orderId;
        if (!orderId || !(this._selectedProvider || this.isCashPayment)) {
          return EMPTY;
        }

        this.logger.info(`Payment START order id='${orderId}, gift amount ='${GiftCard.getTotalCardsAmount(redeemedGiftCards)}'`);

        if (this.shopSettingsService.isPosOrderMode) {
          return this._startPaymentOrder(orderId, new PrepaidPaymentInfo([], [], 0));
        }

        return this._prepaidAndPaymentOrder(orderId, redeemedGiftCards);
      }),
    );
  }

  private _prepaidAndPaymentOrder(orderId: number, redeemedGiftCards: GiftCard[]): Observable<boolean> {
    return of(false).pipe(
      switchMap(_ => {
        if (this.partnerStateService.loggedInPartner == null) {
          return this.paymentService.prepaidGuestOrder(orderId, redeemedGiftCards, this.discountStateService.discountCoupons);
        }
        return this.paymentService.prepaidOrder(orderId, redeemedGiftCards, this.discountStateService.discountCoupons);
      }),
      switchMap(prepaidPaymentInfo => {
        this.logger.info(`Prepaid payment END, order id='${orderId}', prepaid amount=${prepaidPaymentInfo.totalCardsAmount}`);
        this.paymentGiftStateService.loadRedeemedGiftCards(prepaidPaymentInfo.giftCards);
        this.discountStateService.loadCoupons(prepaidPaymentInfo.discountCoupon);
        return this._startPaymentOrder(orderId, prepaidPaymentInfo);
      }),
    );
  }

  private _startPaymentOrder(orderId: number, prepaidPaymentInfo: PrepaidPaymentInfo): Observable<boolean> {
    return of(false).pipe(
      switchMap(_ => {
        if (!orderId || !prepaidPaymentInfo) {
          return EMPTY;
        }
        if (orderId !== -1) {
          this.orderStateService.orderRemoveFromLocalStore();
        }

        if (this._isOrderAmountEqualsPrepaidPayment(prepaidPaymentInfo)) {
          this.logger.info(`Pay order by gift card START, order id='${orderId}'`);
          return this._payOrderByGiftCard(orderId).pipe(
            map(result => {
              if (result) {
                this._paymentFormHtml = '';
                this.orderStateService.order.coupons = this.discountStateService.discountCoupons.map(x => x.clone());
                return true;
              }
            }),
          );
        }

        if (this.isCashPayment) {
          return this.paymentService.payOrderByCash(orderId).pipe(
            map(result => {
              if (result) {
                return true;
              }
            }),
          );
        }

        if (this._selectedProvider) {
          return this._paymentTransaction(orderId, this._selectedProvider.id)
            .pipe(
              map(paymentFormHtml => {
                this.orderStateService.sessionOrderId = orderId;
                // PAYMENT START  !!!
                this._paymentFormHtml = paymentFormHtml;
                return false;
              }),
            );
        }

        return EMPTY;
      })
    );
  }

  private _payOrderByGiftCard(orderId: number): Observable<boolean> {
    if (this.partnerStateService.loggedInPartner == null) {
      return this.paymentService.guestPayOrderByGiftCard(orderId).pipe(
        map(result => {
          this.orderStateService.guestOrderKey = result;
          return !!result;
        })
      );
    }
    return this.paymentService.payOrderByGiftCard(orderId);
  }

  private _paymentTransaction(orderId: number, paymentProviderId: number): Observable<string> {
    if (this.partnerStateService.loggedInPartner == null) {
      const order = this.orderStateService.order;
      return this.paymentService.paymentGuestTransaction(orderId, order?.guestOrderKey, paymentProviderId);
    } else {
      return this.paymentService.paymentTransaction(orderId, paymentProviderId);
    }
  }

  get selectedProvider(): PaymentProvider {
    return this._selectedProvider;
  }

  set selectedProvider(provider: PaymentProvider) {
    this._selectedProvider = provider;
    if (provider.extraPayAmount) {
      this.extraPaymentLine = this.giftCardStepService.addGiftCardCustomPrice(provider.extraPayAmount);
    } else {
      this.deleteExtraPayLine();
    }
  }

  deleteExtraPayLine(): void {
    const order = this.orderStateService.order;
    if (this.extraPaymentLine && order) {
      order.deleteOrderLine(this.extraPaymentLine);
      this.extraPaymentLine = null;
    }
  }


  set isCashPayment(value: boolean) {
    this._isCashPayment = value;
  }

  get isCashPayment(): boolean {
    return this._isCashPayment;
  }

  get totalAmountPrepaid(): number {
    return this.paymentGiftStateService.totalAmountRedeemedGiftCards + this.discountStateService.totalAmountDiscountCoupons;
  }

  reset(): void {
    this._selectedProvider = null;
    this._paymentFormHtml = '';
    this._paymentEnabled = this.orderStateService.order?.totalQuantity > 0;
    this.discountStateService.reset();
    this.paymentGiftStateService.reset();
  }

  private _isOrderAmountEqualsPrepaidPayment(prepaidPaymentInfo: PrepaidPaymentInfo): boolean {
    let amount = this._order.totalAmount - prepaidPaymentInfo.discountCouponAmount;

    if (prepaidPaymentInfo.depositAmount) {
      amount = Math.min(amount, prepaidPaymentInfo.depositAmount);
    }

    if (amount) {
      amount = amount - prepaidPaymentInfo.giftCardsAmount;
    }
    return this._order && !this._order.isPaid && Math.abs(amount) < 0.00000001;
  }

  getProviderIcon(paymentProvider: PaymentProvider): IconDefinition | IconDefinitionFas {
    if (paymentProvider == null || paymentProvider.name === null || paymentProvider.name === '') {
      return null;
    }
    const name = paymentProvider.name.toLowerCase();
    switch (name) {
      case 'cash':
        return faMoneyBillAlt;
      case 'credit card':
      case 'ogone':
      case 'payone':
        return faCreditCard;
      case 'paypal':
        return faCcPaypal;
      case 'account':
        return faIdCard;
      default:
        return null;
    }
  }

  private _startAccountPayment(): Observable<any> {
    return of(true).pipe(
      switchMap(_ => {
        if (this.amountToPay === 0) {
          return this._startPayment();
        }

        if (this.amountToPay < 0) {
          return EMPTY;
        }

        return this.partnerCodeStateService.partnerAccountPayment(this.amountToPay).pipe(
          switchMap(
            partnerCodeCardNumber => {
              if (!partnerCodeCardNumber) {
                this.paymentEnabled = true;
                throw Error('Not enough money in account for payment');
              }

              this.partnerCodeCardNumber = partnerCodeCardNumber;
              return this.paymentGiftStateService.addGiftCardForRedeem(
                this.orderStateService.order.id,
                partnerCodeCardNumber,
                this.amountToPay,
              );
            }),
          switchMap(
            isCardAccepted => {
              if (!isCardAccepted) {
                this.paymentEnabled = true;
                throw Error('Account Payment not accepted');
              }
              return this._startPayment();
            },
          )
        );
      })
    );
  }

  get amountToPay(): number {
    if (!this.orderStateService.order) {
      return 0;
    }

    return this.amountToPayWithoutGiftCards - this.paymentGiftStateService.totalAmountRedeemedGiftCards;
  }

  get amountToPayWithoutGiftCards(): number {
    if (!this.orderStateService.order) {
      return 0;
    }

    return this.orderStateService.order.totalAmount - this.discountStateService.totalAmountDiscountCoupons;
  }

  get name(): string {
    return PaymentStateService.name;
  }

  get paymentEnabled(): boolean {
    return this._paymentEnabled;
  }

  set paymentEnabled(value: boolean) {
    if (value === this._paymentEnabled) {
      return;
    }

    this._paymentEnabled = value;
  }

  get isPartnerCodePaymentAllowed(): boolean {
    return this.shopSettingsService.shopSettings
      && this.shopSettingsService.shopSettings.partnerCodePayment
      && this.orderStateService.order
      && this.orderStateService.order.orderType !== OrderTypeEnum.Giftcard;
  }
}
